import { useEffect, useRef, type RefObject } from 'react';

/**
 * Поведінка спливного меню панелі: закрити кліком повз, закрити Esc і ходити
 * пунктами стрілками.
 *
 * Меню в панелі однакові на вигляд, тож і поводитися мають однаково — тому
 * правила зібрані тут, а не переписані в кожній сторінці. Посилання
 * повертається на обгортку («.viewmenu»): у ній і кнопка, і саме меню, тож
 * клік по кнопці меню не закриває.
 */
export function useMenu(open: boolean, close: () => void): RefObject<HTMLDivElement | null> {
  const box = useRef<HTMLDivElement>(null);
  // Закриття тримаємо в посиланні: інакше слухачі перепідписувалися б на
  // кожен перемальовок сторінки.
  const onClose = useRef(close);
  onClose.current = close;

  useEffect(() => {
    if (!open) return;
    const root = box.current;
    const close = (): void => onClose.current();

    /** Пункти, якими можна ходити: рядок-кнопка, прапорець, поле в меню. */
    const items = (): HTMLElement[] =>
      [...(root?.querySelectorAll<HTMLElement>('button.vpop-row, .vpop-row input, .vpop-range input') ?? [])];

    const outside = (e: MouseEvent): void => {
      if (!root?.contains(e.target as Node)) close();
    };

    const keys = (e: KeyboardEvent): void => {
      if (!root?.contains(e.target as Node) && e.key !== 'Escape') return;
      if (e.key === 'Escape') {
        close();
        // Фокус повертаємо на кнопку, інакше він лишається в нікуди.
        root?.querySelector<HTMLElement>('button')?.focus();
        return;
      }
      if (e.key === 'Tab') {
        close();
        return;
      }
      const list = items();
      if (!list.length) return;
      const at = list.indexOf(document.activeElement as HTMLElement);
      const step = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
      if (step) {
        e.preventDefault();
        // З кнопки меню стрілка вниз веде на перший пункт, вгору — на останній.
        const next = at < 0 ? (step > 0 ? 0 : list.length - 1) : (at + step + list.length) % list.length;
        list[next]?.focus();
        return;
      }
      if (e.key === 'Home' || e.key === 'End') {
        e.preventDefault();
        (e.key === 'Home' ? list[0] : list.at(-1))?.focus();
      }
    };

    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', keys);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', keys);
    };
  }, [open, close]);

  return box;
}
